import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Sparkles, Users, ShieldCheck } from "lucide-react";

const STEPS = [
  {
    n: "01",
    Icon: MessageSquare,
    title: "Tell us your stage",
    body: "A 2-minute questionnaire (or a 30-minute call) — no pressure, no pitch.",
  },
  {
    n: "02",
    Icon: Sparkles,
    title: "See personalized recommendations",
    body: "Education-first. We show you the 2–3 strongest product fits for where you are.",
  },
  {
    n: "03",
    Icon: Users,
    title: "Meet your advisor",
    body: "Each of our four specialists leads a specific chapter of your financial life.",
  },
  {
    n: "04",
    Icon: ShieldCheck,
    title: "Build and protect your plan",
    body: "We handle underwriting, delivery, and annual reviews so the plan ages with you.",
  },
];

export const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-20 sm:py-28 bg-lw-ivory-warm"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="lw-eyebrow">How It Works</span>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]">
            Four steps, <span className="italic">no pressure.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-lw-muted">
            Everything we do is education-first. You never sign anything on a
            first call. You decide when, or whether, to move forward.
          </p>
        </div>

        <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {STEPS.map(({ n, Icon, title, body }, i) => (
            <motion.li
              key={n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 0.61, 0.36, 1] }}
              className="lw-card p-6 sm:p-7 h-full flex flex-col"
              data-testid={`how-step-${n}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-lw-navy text-lw-gold flex items-center justify-center">
                  <Icon size={20} />
                </div>
                <span className="font-serif-display text-3xl text-lw-gold">{n}</span>
              </div>
              <h3 className="mt-5 font-serif-display text-lg text-lw-navy leading-snug">
                {title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-lw-muted flex-1">{body}</p>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default HowItWorks;
